/** Applications group (canonical `CI935tE1`). Application lifecycle writes
 *  (Brief 03) + the authority picker. There is no general update endpoint — each
 *  mutable field has its own targeted PATCH (status, closed_at, drawing link,
 *  reference). After each write, re-fetch the Jobs /detail (it recomputes
 *  applications[], deadlines, counts, flags). */

import { api } from "./client";
import type {
  ApplicationCreateInput,
  ApplicationCreateResponse,
  ApplicationSetStatusResponse,
  ApplicationCloseResponse,
  AuthorityListResponse,
  AuthorityRow,
  OpenApplicationsResponse,
  ReviewQueueResponse,
  LinkAppToJobResponse,
  ApplicationMergePreview,
  ApplicationMergeResult,
} from "@/types";

/** GET /authority/list — LPA (`kind=lpa`) or BCB (`kind=bcb`) picker, active by
 *  default. Unwraps the { items } envelope, tolerating a bare array. */
export async function listAuthorities(
  kind: "lpa" | "bcb" | "other",
  activeOnly = true,
): Promise<AuthorityRow[]> {
  const res = await api.get<AuthorityListResponse | AuthorityRow[]>("apps", "authority/list", {
    query: { kind, active_only: activeOnly },
  });
  return Array.isArray(res) ? res : res?.items ?? [];
}

/** GET /open_applications — the cross-job Open applications feed: every still-open
 *  application bucketed into `planning` + `building_control` (merge soft-deletes
 *  excluded), each carrying its compact parent `job`. No params; read-only. */
export function listOpenApplications(): Promise<OpenApplicationsResponse> {
  return api.get<OpenApplicationsResponse>("apps", "open_applications");
}

/** POST /create — create an application under a job (status starts "draft"). */
export function createApplication(input: ApplicationCreateInput): Promise<ApplicationCreateResponse> {
  return api.post<ApplicationCreateResponse>("apps", "create", { body: input });
}

/** PATCH /application/set_status — set lifecycle status (never `closed`/`draft`
 *  from the UI). */
export function setApplicationStatus(id: number, status: string): Promise<ApplicationSetStatusResponse> {
  return api.patch<ApplicationSetStatusResponse>("apps", "application/set_status", {
    body: { id, status },
  });
}

/** PATCH /close — close (default) or reopen (`unclose:true`). The response lists
 *  the application's still-open linked tasks for the cancel prompt. */
export function closeApplication(id: number, unclose = false): Promise<ApplicationCloseResponse> {
  return api.patch<ApplicationCloseResponse>("apps", "close", {
    body: unclose ? { id, unclose: true } : { id },
  });
}

/** PATCH /application/set_drawing — link/unlink the drawing whose submit task this
 *  application closes when submitted (Addendum 2). `drawing_id: 0` unlinks. Fires
 *  recompute; re-fetch /detail after. NOTE: requires the matching Xano endpoint
 *  (mirrors set_status; validates the drawing belongs to the job + type matches). */
export function setApplicationDrawing(id: number, drawingId: number): Promise<unknown> {
  return api.patch<unknown>("apps", "application/set_drawing", { body: { id, drawing_id: drawingId } });
}

/** PATCH /application/set_reference — set or correct the application's reference
 *  (e.g. once the LPA/BCB validates and issues the number you didn't have at
 *  creation). Trimmed server-side; blank clears it. Rejects a reference already
 *  used by another live application under the same authority (mirrors create's
 *  uniqueness guard). Fires recompute; re-fetch /detail after. */
export function setApplicationReference(id: number, appReference: string): Promise<unknown> {
  return api.patch<unknown>("apps", "application/set_reference", {
    body: { id, app_reference: appReference },
  });
}

/** PATCH /application/edit_view_url — set/amend the council-portal deep link
 *  (`application_view_url`) by hand. Trimmed server-side; blank clears it; a
 *  non-empty value must be a full http(s):// URL (it's rendered as an href, so
 *  the server rejects other schemes). This is the editable staff path — distinct
 *  from the machine `set_view_url` writeback, which is secret-gated and only ever
 *  fills a blank. Fires recompute; re-fetch /detail after. */
export function setApplicationViewUrl(id: number, viewUrl: string): Promise<unknown> {
  return api.patch<unknown>("apps", "application/edit_view_url", {
    body: { id, application_view_url: viewUrl },
  });
}

/** PATCH /application/set_resubmission — mark another of the job's planning
 *  applications as the resubmission of a refused (or withdrawn) one. Sets
 *  supersedes_application_id on the NEWER app so consent_totals collapses the
 *  chain into one thread. Atomic: any sibling currently linked to `refusedId`
 *  is cleared first, so at most one live resubmission exists per application.
 *  `resubmissionId: 0` clears. Rejects cross-job / non-planning targets and a
 *  target already superseding a different application. Re-fetch /detail after. */
export function setApplicationResubmission(refusedId: number, resubmissionId: number): Promise<unknown> {
  return api.patch<unknown>("apps", "application/set_resubmission", {
    body: { id: refusedId, resubmission_id: resubmissionId },
  });
}

/* ─── Review & Merge (Phase 1) ─────────────────────────────────────────────
 * The reconciliation triage feed + its one write. Record merge/dismiss are
 * Phase 1b and not exposed here yet. */

/** GET /application/review-queue — read-only triage feed: unmatched applications
 *  (with suggested jobs/siblings) + duplicate groups. No params. */
export function getReviewQueue(): Promise<ReviewQueueResponse> {
  return api.get<ReviewQueueResponse>("apps", "application/review-queue");
}

/** POST /application/link_to_job — the review-queue "match to job" action: sets
 *  job_id + match_status="matched". 404 if app/job missing; 400 if the target job
 *  was merged away. After it, re-fetch the queue (the app leaves `unmatched`). */
export function linkAppToJob(applicationId: number, jobId: number): Promise<LinkAppToJobResponse> {
  return api.post<LinkAppToJobResponse>("apps", "application/link_to_job", {
    body: { application_id: applicationId, job_id: jobId },
  });
}

/** POST /application/merge with dry_run — the no-write preview: who wins, who's
 *  merged in, what gets repointed/backfilled. Use it to confirm before committing. */
export function previewApplicationMerge(
  applicationIds: number[],
  allowOrphanLoser: boolean,
): Promise<ApplicationMergePreview> {
  return api.post<ApplicationMergePreview>("apps", "application/merge", {
    body: { application_ids: applicationIds, dry_run: true, allow_orphan_loser: allowOrphanLoser },
  });
}

/** POST /application/merge — commits the merge (repoints letters/fees/tasks to the
 *  winner, backfills it, tombstones the losers). Destructive; gate behind the
 *  dry-run preview + a confirm. Re-fetch the queue after (merged losers drop out). */
export function mergeApplications(
  applicationIds: number[],
  allowOrphanLoser: boolean,
): Promise<ApplicationMergeResult> {
  return api.post<ApplicationMergeResult>("apps", "application/merge", {
    body: { application_ids: applicationIds, allow_orphan_loser: allowOrphanLoser },
  });
}
